import type { FeToolResult } from "@/services/agent/feToolRegistry";
import type { AgentSurfaceActionHandler, AgentSurfaceRegistration } from "./types";

type PendingSurfaceAction = {
  surfaceId: string;
  actionId: string;
  params: Record<string, unknown>;
};

class AgentSurfaceRegistry {
  private surfaces = new Map<string, AgentSurfaceRegistration>();
  private pendingActions: PendingSurfaceAction[] = [];

  register(registration: AgentSurfaceRegistration): () => void {
    this.surfaces.set(registration.surfaceId, registration);
    this.flushPendingForSurface(registration.surfaceId);
    return () => {
      const current = this.surfaces.get(registration.surfaceId);
      if (current === registration) {
        this.surfaces.delete(registration.surfaceId);
      }
    };
  }

  getLiveContext(surfaceId: string): Record<string, unknown> | null {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) {
      return null;
    }
    return surface.getLiveContext();
  }

  mergeLiveContexts(surfaceIds: string[]): Record<string, unknown> {
    const merged: Record<string, unknown> = {};
    for (const surfaceId of surfaceIds) {
      const ctx = this.getLiveContext(surfaceId);
      if (ctx) {
        Object.assign(merged, ctx);
      }
    }
    return merged;
  }

  async execute(
    surfaceId: string,
    actionId: string,
    params: Record<string, unknown> = {},
  ): Promise<FeToolResult> {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) {
      return {
        success: false,
        message: `Surface "${surfaceId}" is not mounted — open the app first.`,
      };
    }
    const handler = surface.actions[actionId];
    if (!handler) {
      return {
        success: false,
        message: `Unknown action "${actionId}" on surface "${surfaceId}".`,
      };
    }
    try {
      return await handler(params);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Surface action failed",
      };
    }
  }

  queuePending(surfaceId: string, actionId: string, params: Record<string, unknown>): void {
    this.pendingActions.push({ surfaceId, actionId, params });
  }

  consumePending(surfaceId: string, actionId: string): Record<string, unknown> | null {
    const index = this.pendingActions.findIndex(
      (pending) => pending.surfaceId === surfaceId && pending.actionId === actionId,
    );
    if (index < 0) {
      return null;
    }
    const [pending] = this.pendingActions.splice(index, 1);
    return pending.params;
  }

  hasPending(surfaceId?: string): boolean {
    if (!surfaceId) {
      return this.pendingActions.length > 0;
    }
    return this.pendingActions.some((pending) => pending.surfaceId === surfaceId);
  }

  private flushPendingForSurface(surfaceId: string): void {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) {
      return;
    }
    const toRun = this.pendingActions.filter((pending) => pending.surfaceId === surfaceId);
    this.pendingActions = this.pendingActions.filter((pending) => pending.surfaceId !== surfaceId);
    for (const pending of toRun) {
      const handler = surface.actions[pending.actionId];
      if (handler) {
        void handler(pending.params);
      } else {
        this.pendingActions.push(pending);
      }
    }
  }
}

export const agentSurfaceRegistry = new AgentSurfaceRegistry();

export function registerAgentSurface(registration: AgentSurfaceRegistration): () => void {
  return agentSurfaceRegistry.register(registration);
}

export async function executeAgentSurfaceAction(
  surfaceId: string,
  actionId: string,
  params: Record<string, unknown> = {},
): Promise<FeToolResult> {
  return agentSurfaceRegistry.execute(surfaceId, actionId, params);
}

export type { AgentSurfaceActionHandler, AgentSurfaceRegistration };
